import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Tractor,
    Spade,
    Truck,
    MapPinLine,
    Phone,
    EnvelopeSimple,
    ArrowUpRight,
    List,
    X,
    HardHat,
    Path,
    TreeEvergreen,
    Stack,
    PaperPlaneTilt,
    FacebookLogo,
    InstagramLogo,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO =
    "https://customer-assets.emergentagent.com/job_earthwork-preview/artifacts/wyzkelmj_Messenger_creation_83E98FE1-3D41-4016-9F16-F2731D827504.png";
const HERO_IMG =
    "https://customer-assets.emergentagent.com/job_2b193db6-e0df-422b-958a-7d2579ab571b/artifacts/4llamaf2_20250425_192100.jpg";
const GALLERY = [
    {
        url: "https://customer-assets.emergentagent.com/job_2b193db6-e0df-422b-958a-7d2579ab571b/artifacts/4llamaf2_20250425_192100.jpg",
        label: "Hitachi Zaxis 200 — Bulk Earthworks",
        alt: "Hitachi Zaxis 200 excavator on a bulk earthworks site, West Coast New Zealand",
    },
    {
        url: "https://customer-assets.emergentagent.com/job_2b193db6-e0df-422b-958a-7d2579ab571b/artifacts/fvksbpvu_20231218_115644.jpg",
        label: "Residential Trenching",
        alt: "Mini excavator digging a residential trench for water and power, Greymouth",
    },
    {
        url: "https://customer-assets.emergentagent.com/job_2b193db6-e0df-422b-958a-7d2579ab571b/artifacts/4wuzen4g_20230808_092318.jpg",
        label: "Compaction & Site Prep",
        alt: "Roller compactor finishing a building pad during site preparation, Hokitika",
    },
    {
        url: "https://customer-assets.emergentagent.com/job_2b193db6-e0df-422b-958a-7d2579ab571b/artifacts/i4i2cyuv_Snapchat-701505179.jpg",
        label: "Land Clearing — Acreage",
        alt: "Cleared and contoured paddock after land clearing, West Coast NZ",
    },
];

const FACEBOOK_URL = "https://www.facebook.com/share/1RSRRKKSqH/";
const INSTAGRAM_URL =
    "https://www.instagram.com/kingcontracting_?igsh=ajNuODN1MXkzYXhi";

const SERVICES = [
    {
        title: "Earthworks",
        desc: "Bulk excavation, cut and fill, retaining and foundation digs across the West Coast — Greymouth, Hokitika, Westport and Reefton.",
        Icon: Tractor,
    },
    {
        title: "Trenching",
        desc: "Clean, accurate trenching for water, power and fibre — residential to rural sites across the West Coast NZ.",
        Icon: Spade,
    },
    {
        title: "Site Prep",
        desc: "Levelling, hardfill, compaction and ready-to-build pads for new homes, sheds and farm buildings throughout the West Coast.",
        Icon: HardHat,
    },
    {
        title: "Driveways",
        desc: "Formed and metalled driveways, culverts and accessways built to last on West Coast terrain — Greymouth to Hokitika.",
        Icon: Path,
    },
    {
        title: "Section Clearing",
        desc: "Stripping overgrown sections, scrub and stumps in Greymouth, Hokitika and Westport so your block is ready to use.",
        Icon: TreeEvergreen,
    },
    {
        title: "Land Clearing",
        desc: "Paddock and acreage clearing, mulching and contouring for West Coast farms and lifestyle blocks.",
        Icon: Stack,
    },
    {
        title: "Landscaping",
        desc: "Shaping and finishing — terracing, garden beds, pads and tidy-ups, ready to plant or pave.",
        Icon: Truck,
    },
];

/* ============================================================ */
/* Header                                                        */
/* ============================================================ */
function Header({ onNav }) {
    const [open, setOpen] = useState(false);
    const links = [
        ["Services", "services"],
        ["Work", "gallery"],
        ["About", "about"],
        ["Contact", "contact"],
    ];

    const go = (id) => {
        setOpen(false);
        onNav(id);
    };

    return (
        <header
            className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10"
            data-testid="site-header"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 sm:h-24 flex items-center justify-between">
                <button
                    onClick={() => go("top")}
                    className="flex items-center gap-3"
                    data-testid="header-logo-btn"
                >
                    <img
                        src={LOGO}
                        alt="King Contracting 2024 Ltd"
                        className="h-14 sm:h-20 w-auto object-contain"
                    />
                </button>

                <nav className="hidden md:flex items-center gap-8">
                    {links.map(([label, id]) => (
                        <button
                            key={id}
                            onClick={() => go(id)}
                            className="font-body text-sm uppercase tracking-[0.2em] text-zinc-300 hover:text-[#e11a1a] transition-colors"
                            data-testid={`nav-${id}`}
                        >
                            {label}
                        </button>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <button
                        onClick={() => go("contact")}
                        className="kc-btn-primary hidden sm:inline-flex"
                        data-testid="header-cta-quote"
                    >
                        Get an Estimate
                        <ArrowUpRight size={16} weight="bold" />
                    </button>
                    <button
                        onClick={() => setOpen(!open)}
                        className="md:hidden p-2 border border-white/10 text-white"
                        data-testid="mobile-menu-toggle"
                        aria-label="Menu"
                    >
                        {open ? <X size={20} /> : <List size={20} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="md:hidden border-t border-white/10 bg-black/95">
                    <div className="px-4 py-4 flex flex-col gap-1">
                        {links.map(([label, id]) => (
                            <button
                                key={id}
                                onClick={() => go(id)}
                                className="text-left py-3 px-2 font-body uppercase tracking-[0.2em] text-zinc-200 border-b border-white/5"
                                data-testid={`mobile-nav-${id}`}
                            >
                                {label}
                            </button>
                        ))}
                        <a
                            href="tel:0273264298"
                            className="kc-btn-primary mt-3 justify-center"
                            data-testid="mobile-cta-call"
                        >
                            <Phone size={16} weight="bold" /> Call 027 326 4298
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}

/* ============================================================ */
/* Hero                                                          */
/* ============================================================ */
function Hero({ onNav }) {
    return (
        <section
            id="top"
            className="relative border-b border-white/10"
            data-testid="hero-section"
        >
            {/* MOBILE: stacked photo + content (photo always visible) */}
            <div className="sm:hidden">
                <div className="relative w-full aspect-[4/3] overflow-hidden">
                    <img
                        src={HERO_IMG}
                        alt="Hitachi excavator on West Coast site"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/70" />
                    <div className="absolute top-4 left-4 flex items-center gap-3">
                        <div className="h-px w-8 bg-[#e11a1a]" />
                        <span className="kc-section-num">
                            01 / WEST COAST · NZ
                        </span>
                    </div>
                </div>
                <div className="bg-black px-4 py-10 kc-rise">
                    <h1
                        className="font-display uppercase text-white leading-[0.9] tracking-tight"
                        style={{ fontSize: "clamp(2.5rem, 11vw, 4rem)" }}
                        data-testid="hero-headline-mobile"
                    >
                        Earthworks <span className="text-[#e11a1a]">·</span>{" "}
                        Trenching
                        <br />
                        Site Prep{" "}
                        <span className="text-[#e11a1a]">·</span>{" "}
                        Done&nbsp;Right.
                    </h1>
                    <p className="mt-5 text-zinc-300 text-base leading-relaxed">
                        Owner-operated earthworks and excavation
                        contractor servicing Greymouth, Hokitika, Westport,
                        Reefton and the wider West Coast — from Karamea to
                        Haast. From a single trench in town to acreage
                        clearing and full site builds, we turn up and we
                        get it done.
                    </p>
                    <div className="mt-8 flex flex-col gap-3">
                        <button
                            onClick={() => onNav("contact")}
                            className="kc-btn-primary justify-center"
                            data-testid="hero-cta-quote-mobile"
                        >
                            Get a Free Estimate
                            <ArrowUpRight size={16} weight="bold" />
                        </button>
                        <a
                            href="tel:0273264298"
                            className="kc-btn-outline justify-center"
                            data-testid="hero-cta-call-mobile"
                        >
                            <Phone size={16} weight="bold" />
                            027 326 4298
                        </a>
                    </div>
                </div>
            </div>

            {/* DESKTOP: full-bleed overlay hero */}
            <div className="hidden sm:flex relative min-h-[88vh] items-end overflow-hidden">
                <img
                    src={HERO_IMG}
                    alt="Hitachi excavator on West Coast site"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
                <div className="absolute inset-0 bg-black/30" />

                <div className="relative max-w-7xl mx-auto w-full px-6 lg:px-8 pt-32 pb-24 z-10">
                    <div className="kc-rise max-w-4xl">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="h-px w-12 bg-[#e11a1a]" />
                            <span className="kc-section-num">
                                01 / WEST COAST · NZ
                            </span>
                        </div>
                        <h1
                            className="font-display uppercase text-white leading-[0.9] tracking-tight"
                            style={{
                                fontSize: "clamp(3rem, 9vw, 7.5rem)",
                            }}
                            data-testid="hero-headline"
                        >
                            Earthworks{" "}
                            <span className="text-[#e11a1a]">·</span>{" "}
                            Trenching
                            <br />
                            Site Prep{" "}
                            <span className="text-[#e11a1a]">·</span>{" "}
                            Done&nbsp;Right.
                        </h1>
                        <p
                            className="mt-6 max-w-2xl text-zinc-300 text-lg leading-relaxed"
                            data-testid="hero-subhead"
                        >
                            Owner-operated earthworks and excavation
                            contractor servicing Greymouth, Hokitika,
                            Westport, Reefton and the wider West Coast —
                            from Karamea to Haast. From a single trench
                            in town to acreage clearing and full site
                            builds, we turn up and we get it done.
                        </p>

                        <div className="mt-10 flex flex-wrap gap-4">
                            <button
                                onClick={() => onNav("contact")}
                                className="kc-btn-primary"
                                data-testid="hero-cta-quote"
                            >
                                Get a Free Estimate
                                <ArrowUpRight size={16} weight="bold" />
                            </button>
                            <a
                                href="tel:0273264298"
                                className="kc-btn-outline"
                                data-testid="hero-cta-call"
                            >
                                <Phone size={16} weight="bold" />
                                027 326 4298
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ============================================================ */
/* Marquee strip                                                 */
/* ============================================================ */
function Marquee() {
    const items = [
        "EARTHWORKS",
        "TRENCHING",
        "SITE PREP",
        "DRIVEWAYS",
        "SECTION CLEARING",
        "LAND CLEARING",
        "LANDSCAPING",
        "WEST COAST NZ",
    ];
    const row = [...items, ...items];
    return (
        <div
            className="border-b border-white/10 bg-black overflow-hidden"
            data-testid="marquee-strip"
        >
            <div className="kc-marquee-track flex whitespace-nowrap py-5">
                {row.map((t, i) => (
                    <span
                        key={i}
                        className="font-display text-3xl sm:text-5xl tracking-wide px-8 text-zinc-200"
                    >
                        {t}{" "}
                        <span className="text-[#e11a1a] mx-4">
                            ✕
                        </span>
                    </span>
                ))}
            </div>
        </div>
    );
}

/* ============================================================ */
/* Services                                                      */
/* ============================================================ */
function Services({ onNav }) {
    return (
        <section
            id="services"
            className="relative py-24 sm:py-32 border-b border-white/10"
            data-testid="services-section"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-12 gap-10 mb-14">
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="h-px w-10 bg-[#e11a1a]" />
                            <span className="kc-section-num">
                                02 / SERVICES
                            </span>
                        </div>
                        <h2
                            className="font-display uppercase text-white leading-[0.95]"
                            style={{ fontSize: "clamp(2.25rem, 5vw, 4rem)" }}
                        >
                            What we&apos;re
                            <br />
                            built for.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 lg:pt-4">
                        <p className="text-zinc-300 text-base sm:text-lg leading-relaxed max-w-2xl">
                            Seven core services — covering everything from
                            mini-excavator trenching jobs in town to full
                            paddock clearing on the farm. If it involves
                            dirt, gravel or shaping land, we&apos;ve got the
                            gear and the operators for it.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
                    {SERVICES.map(({ title, desc, Icon }, i) => (
                        <div
                            key={title}
                            className="kc-surface p-8 sm:p-10 group cursor-default"
                            data-testid={`service-card-${i}`}
                        >
                            <div className="flex items-start justify-between mb-8">
                                <Icon
                                    size={44}
                                    weight="duotone"
                                    className="text-[#e11a1a]"
                                />
                                <span className="kc-section-num">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                            </div>
                            <h3 className="font-display text-3xl uppercase tracking-wide text-white mb-3">
                                {title}
                            </h3>
                            <p className="text-zinc-400 text-sm leading-relaxed">
                                {desc}
                            </p>
                        </div>
                    ))}
                    <button
                        onClick={() => onNav("contact")}
                        className="kc-surface p-8 sm:p-10 text-left flex flex-col justify-between bg-[#e11a1a] hover:bg-[#b91212]"
                        style={{ borderColor: "#e11a1a" }}
                        data-testid="service-card-cta"
                    >
                        <ArrowUpRight
                            size={44}
                            weight="bold"
                            className="text-white"
                        />
                        <div>
                            <h3 className="font-display text-3xl uppercase tracking-wide text-white mb-2">
                                Got a Job?
                            </h3>
                            <p className="text-white/80 text-sm">
                                Tell us about it — estimates are free.
                            </p>
                        </div>
                    </button>
                </div>
            </div>
        </section>
    );
}

/* ============================================================ */
/* Gallery                                                       */
/* ============================================================ */
function Gallery() {
    return (
        <section
            id="gallery"
            className="relative py-24 sm:py-32 border-b border-white/10"
            data-testid="gallery-section"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
                    <div>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="h-px w-10 bg-[#e11a1a]" />
                            <span className="kc-section-num">
                                03 / RECENT WORK
                            </span>
                        </div>
                        <h2
                            className="font-display uppercase text-white leading-[0.95]"
                            style={{ fontSize: "clamp(2.25rem, 5vw, 4rem)" }}
                        >
                            On the tools.
                        </h2>
                    </div>
                    <p className="text-zinc-400 max-w-md text-sm sm:text-base">
                        A look at recent jobs across the West Coast — from
                        residential trenching to bulk earthworks and acreage
                        clearing.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-12 sm:grid-rows-2 gap-4 sm:gap-6 sm:h-[720px]">
                    <GalleryTile
                        item={GALLERY[0]}
                        className="aspect-[4/3] sm:aspect-auto sm:col-span-8 sm:row-span-2"
                        idx={0}
                    />
                    <GalleryTile
                        item={GALLERY[1]}
                        className="aspect-[4/3] sm:aspect-auto sm:col-span-4 sm:row-span-1"
                        idx={1}
                    />
                    <GalleryTile
                        item={GALLERY[2]}
                        className="aspect-[4/3] sm:aspect-auto sm:col-span-2 sm:row-span-1"
                        idx={2}
                    />
                    <GalleryTile
                        item={GALLERY[3]}
                        className="aspect-[4/3] sm:aspect-auto sm:col-span-2 sm:row-span-1"
                        idx={3}
                    />
                </div>
            </div>
        </section>
    );
}

function GalleryTile({ item, className, idx }) {
    return (
        <figure
            className={`relative overflow-hidden border border-white/10 group ${className}`}
            data-testid={`gallery-tile-${idx}`}
        >
            <img
                src={item.url}
                alt={item.alt || item.label}
                loading="lazy"
                className="kc-gallery-img absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-90 group-hover:opacity-70 transition-opacity" />
            <figcaption className="absolute bottom-0 left-0 right-0 p-4 sm:p-5 flex items-end justify-between">
                <span className="font-display text-lg sm:text-xl text-white tracking-wide uppercase">
                    {item.label}
                </span>
                <ArrowUpRight
                    size={22}
                    weight="bold"
                    className="text-[#e11a1a] opacity-0 group-hover:opacity-100 transition-opacity"
                />
            </figcaption>
        </figure>
    );
}

/* ============================================================ */
/* About                                                         */
/* ============================================================ */
function About() {
    const stats = [
        { v: "100%", l: "Owner operated" },
        { v: "7", l: "Core services" },
        { v: "WCNZ", l: "Service area" },
    ];
    return (
        <section
            id="about"
            className="relative py-24 sm:py-32 border-b border-white/10"
            data-testid="about-section"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="h-px w-10 bg-[#e11a1a]" />
                        <span className="kc-section-num">04 / ABOUT</span>
                    </div>
                    <h2
                        className="font-display uppercase text-white leading-[0.95] mb-6"
                        style={{ fontSize: "clamp(2.25rem, 5vw, 4rem)" }}
                    >
                        Local crew.
                        <br />
                        <span className="text-[#e11a1a]">Heavy gear.</span>
                        <br />
                        Honest yards.
                    </h2>
                </div>

                <div className="lg:col-span-7 lg:pl-10 lg:border-l lg:border-white/10 space-y-6">
                    <p className="text-zinc-300 text-base sm:text-lg leading-relaxed">
                        King Contracting 2024 Ltd is a West Coast-based
                        earthmoving and excavation contractor servicing
                        Greymouth, Hokitika, Westport, Reefton and
                        surrounding rural communities — from Karamea to
                        Haast. We run our own machinery, from a 1.8t
                        mini excavator that fits down the side of a
                        suburban house, right up to a 20-tonne Hitachi
                        Zaxis for the bigger digs.
                    </p>
                    <p className="text-zinc-400 text-base leading-relaxed">
                        Whether it&apos;s a single-day trenching job in
                        town, a new driveway, a building pad, or clearing
                        a paddock, you&apos;re dealing direct with the
                        operator. No middlemen, no surprises — just
                        turn-up-on-time work and a tidy site at the end of
                        it.
                    </p>

                    <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10 mt-10">
                        {stats.map((s, i) => (
                            <div
                                key={i}
                                className="bg-[#121214] p-5 sm:p-6"
                                data-testid={`about-stat-${i}`}
                            >
                                <div className="font-display text-3xl sm:text-4xl text-white">
                                    {s.v}
                                </div>
                                <div className="kc-eyebrow mt-2 text-[0.62rem]">
                                    {s.l}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ============================================================ */
/* Contact                                                       */
/* ============================================================ */
function Contact() {
    const [form, setForm] = useState({
        name: "",
        phone: "",
        email: "",
        service: "",
        message: "",
    });
    const [busy, setBusy] = useState(false);

    const update = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim() || !form.message.trim()) {
            toast.error("Name, phone and message are required.");
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/enquiries`, form);
            toast.success("Enquiry sent. We'll be in touch shortly.");
            setForm({
                name: "",
                phone: "",
                email: "",
                service: "",
                message: "",
            });
        } catch (err) {
            console.error(err);
            toast.error("Couldn't send. Please call 027 326 4298.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <section
            id="contact"
            className="relative py-24 sm:py-32 border-b border-white/10"
            data-testid="contact-section"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-5">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="h-px w-10 bg-[#e11a1a]" />
                        <span className="kc-section-num">
                            05 / GET IN TOUCH
                        </span>
                    </div>
                    <h2
                        className="font-display uppercase text-white leading-[0.95] mb-8"
                        style={{ fontSize: "clamp(2.25rem, 5vw, 4rem)" }}
                    >
                        Got a job
                        <br />
                        in mind?
                    </h2>

                    <p className="text-zinc-400 mb-10 max-w-md">
                        Drop your details below or call us direct.
                        Free no-obligation estimates anywhere on the
                        West Coast.
                    </p>

                    <div className="space-y-px bg-white/10 border border-white/10">
                        <ContactRow
                            Icon={Phone}
                            label="Call / Text"
                            value="027 326 4298"
                            href="tel:0273264298"
                            tid="contact-phone"
                        />
                        <ContactRow
                            Icon={EnvelopeSimple}
                            label="Email"
                            value="King.contracting@outlook.com"
                            href="mailto:King.contracting@outlook.com"
                            tid="contact-email"
                        />
                        <ContactRow
                            Icon={MapPinLine}
                            label="Service Area"
                            value="West Coast, New Zealand"
                            tid="contact-area"
                        />
                    </div>

                    <div className="mt-8">
                        <div className="kc-eyebrow text-[0.62rem] mb-3">
                            Follow the Work
                        </div>
                        <div className="flex gap-3">
                            <a
                                href={FACEBOOK_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="kc-surface w-12 h-12 flex items-center justify-center text-zinc-300 hover:text-[#e11a1a]"
                                aria-label="Facebook"
                                data-testid="contact-facebook"
                            >
                                <FacebookLogo size={22} weight="fill" />
                            </a>
                            <a
                                href={INSTAGRAM_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="kc-surface w-12 h-12 flex items-center justify-center text-zinc-300 hover:text-[#e11a1a]"
                                aria-label="Instagram"
                                data-testid="contact-instagram"
                            >
                                <InstagramLogo size={22} weight="fill" />
                            </a>
                        </div>
                    </div>
                </div>

                <form
                    onSubmit={submit}
                    className="lg:col-span-7 bg-[#121214] border border-white/10 p-6 sm:p-10"
                    data-testid="enquiry-form"
                >
                    <div className="flex items-center justify-between mb-8">
                        <h3 className="font-display text-2xl sm:text-3xl uppercase tracking-wide text-white">
                            Enquiry Form
                        </h3>
                        <span className="kc-section-num">FORM / 01</span>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
                        <Field label="Name *">
                            <input
                                className="kc-input"
                                value={form.name}
                                onChange={update("name")}
                                placeholder="John Smith"
                                data-testid="form-name"
                            />
                        </Field>
                        <Field label="Phone *">
                            <input
                                className="kc-input"
                                value={form.phone}
                                onChange={update("phone")}
                                placeholder="021 123 4567"
                                data-testid="form-phone"
                            />
                        </Field>
                        <Field label="Email">
                            <input
                                className="kc-input"
                                type="email"
                                value={form.email}
                                onChange={update("email")}
                                placeholder="you@email.com"
                                data-testid="form-email"
                            />
                        </Field>
                        <Field label="Service">
                            <select
                                className="kc-input"
                                value={form.service}
                                onChange={update("service")}
                                data-testid="form-service"
                            >
                                <option value="">Select a service…</option>
                                {SERVICES.map((s) => (
                                    <option key={s.title} value={s.title}>
                                        {s.title}
                                    </option>
                                ))}
                                <option value="Other">Other</option>
                            </select>
                        </Field>
                        <div className="sm:col-span-2">
                            <Field label="Tell us about the job *">
                                <textarea
                                    className="kc-input min-h-[140px] resize-y"
                                    value={form.message}
                                    onChange={update("message")}
                                    placeholder="Site location, what you need done, timeline…"
                                    data-testid="form-message"
                                />
                            </Field>
                        </div>
                    </div>

                    <div className="mt-8 flex flex-wrap items-center gap-4">
                        <button
                            type="submit"
                            disabled={busy}
                            className="kc-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                            data-testid="form-submit"
                        >
                            {busy ? "Sending…" : "Send Enquiry"}
                            <PaperPlaneTilt size={16} weight="bold" />
                        </button>
                        <span className="kc-eyebrow">
                            Free Estimates · No Obligation
                        </span>
                    </div>
                </form>
            </div>
        </section>
    );
}

function ContactRow({ Icon, label, value, href, tid }) {
    const inner = (
        <>
            <Icon
                size={28}
                weight="duotone"
                className="text-[#e11a1a] flex-shrink-0"
            />
            <div className="min-w-0">
                <div className="kc-eyebrow text-[0.62rem]">{label}</div>
                <div className="font-display text-xl sm:text-2xl tracking-wide text-white truncate">
                    {value}
                </div>
            </div>
            {href && (
                <ArrowUpRight
                    size={20}
                    weight="bold"
                    className="ml-auto text-zinc-500 group-hover:text-[#e11a1a] transition-colors"
                />
            )}
        </>
    );
    const cls =
        "bg-[#121214] p-5 sm:p-6 flex items-center gap-4 group transition-colors hover:bg-[#17171a]";
    return href ? (
        <a href={href} className={cls} data-testid={tid}>
            {inner}
        </a>
    ) : (
        <div className={cls} data-testid={tid}>
            {inner}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="kc-eyebrow block mb-2">{label}</span>
            {children}
        </label>
    );
}

/* ============================================================ */
/* Footer                                                        */
/* ============================================================ */
function Footer() {
    return (
        <footer
            className="bg-black border-t border-white/10 py-10"
            data-testid="site-footer"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-3">
                    <img
                        src={LOGO}
                        alt="King Contracting"
                        className="h-12 w-auto object-contain"
                    />
                    <div className="font-display text-lg tracking-wider text-white hidden sm:block">
                        KING CONTRACTING 2024 LTD
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <a
                        href={FACEBOOK_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 flex items-center justify-center border border-white/10 text-zinc-300 hover:text-[#e11a1a] hover:border-[#e11a1a] transition-colors"
                        aria-label="Facebook"
                        data-testid="footer-facebook"
                    >
                        <FacebookLogo size={18} weight="fill" />
                    </a>
                    <a
                        href={INSTAGRAM_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-10 h-10 flex items-center justify-center border border-white/10 text-zinc-300 hover:text-[#e11a1a] hover:border-[#e11a1a] transition-colors"
                        aria-label="Instagram"
                        data-testid="footer-instagram"
                    >
                        <InstagramLogo size={18} weight="fill" />
                    </a>
                </div>

                <div className="kc-eyebrow text-[0.62rem]">
                    © {new Date().getFullYear()} · West Coast, NZ
                </div>
            </div>
        </footer>
    );
}

/* ============================================================ */
/* Page                                                          */
/* ============================================================ */
export default function Home() {
    const scrollTo = (id) => {
        if (id === "top") {
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }
        const el = document.getElementById(id);
        if (el) {
            const y =
                el.getBoundingClientRect().top + window.scrollY - 96;
            window.scrollTo({ top: y, behavior: "smooth" });
        }
    };

    useEffect(() => {
        // simple ping to wake the API
        axios.get(`${API}/`).catch(() => {});
    }, []);

    return (
        <>
            <Header onNav={scrollTo} />
            <main className="pt-20 sm:pt-24" data-testid="home-page">
                <h2 className="sr-only">
                    Earthworks, trenching, driveways, site preparation,
                    section clearing, land clearing and landscaping
                    contractor servicing Greymouth, Hokitika, Westport,
                    Reefton and the wider West Coast of New Zealand —
                    from Karamea to Haast. Owner-operated. Free
                    no-obligation estimates.
                </h2>
                <Hero onNav={scrollTo} />
                <Marquee />
                <Services onNav={scrollTo} />
                <Gallery />
                <About />
                <Contact />
            </main>
            <Footer />
        </>
    );
}
